import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    LinearProgress,
    CircularProgress,
} from "@mui/material";
import axiosInstance from "../../api/axiosInstance";
import CreditWalletPanel from "../billing/CreditWalletPanel";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

// Service config: title, overall key, daily key, dailyAssign key, icon, color
// Matches API: included_services ["personal_whatsapp", "business_email", "business_whatsapp", "bot_credits"]
const SERVICE_CONFIG = {
    business_email: {
        title: "Business Email",
        overallKey: "bec",
        dailyKey: "dbec",
        dailyAssignKey: "adbec",
        icon: <EmailIcon sx={{ fontSize: 24 }} />,
        color: "#1976d2",
    },
    personal_whatsapp: {
        title: "Personal WhatsApp",
        overallKey: "pwc",
        dailyKey: "dwpc",
        dailyAssignKey: "adwpc",
        icon: <WhatsAppIcon sx={{ fontSize: 24 }} />,
        color: "#25d366",
    },
    business_whatsapp: {
        title: "Business WhatsApp",
        overallKey: "bwc",
        dailyKey: "dbwc",
        dailyAssignKey: "adbwc",
        icon: <WhatsAppIcon sx={{ fontSize: 24 }} />,
        color: "#128c7e",
    },
    bot_credits: {
        title: "Bot Credits",
        overallKey: "bot_credits_count",
        dailyKey: null,
        dailyAssignKey: null,
        icon: <SmartToyIcon sx={{ fontSize: 24 }} />,
        color: "#7c3aed",
    },
};

const CreditsInfoCard = ({ serviceKey, config, data }) => {
    const isBotOnly = serviceKey === "bot_credits";

    const overall = data[config.overallKey] ?? 0;
    const daily = config.dailyKey ? (data[config.dailyKey] ?? 0) : 0;
    const dailyAssign = config.dailyAssignKey ? (data[config.dailyAssignKey] ?? 0) : 0;

    const percentRemaining =
        dailyAssign > 0 ? ((daily / dailyAssign) * 100 ).toFixed(2) : 0;
    const percentClamped = Math.min(100, Math.max(0, percentRemaining));

    return (
        <Card
            elevation={0}
            sx={{
                height: "100%",
                background: "rgba(255, 255, 255, 0.6)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                border: "1px solid rgba(255, 255, 255, 0.8)",
                borderRadius: 2,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.06)",
                transition: "all 0.3s ease",
                "&:hover": {
                    background: "rgba(255, 255, 255, 0.75)",
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.1)",
                },
            }}
        >
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                {/* Service Name */}
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 700,
                        color: "text.primary",
                        mb: 2,
                    }}
                >
                    {config.title}
                </Typography>
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: isBotOnly ? 0 : 3,
                    }}
                >
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: 1.5,
                                color: "text.secondary",
                                display: "block",
                                mb: 0.5,
                            }}
                        >
                            Overall Credits
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: "text.primary",
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                {typeof overall === "number" ? overall.toLocaleString() : overall}
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            bgcolor: `${config.color}20`,
                            p: 1.25,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: config.color,
                        }}
                    >
                        {config.icon}
                    </Box>
                </Box>

                {/* Daily section - skip for bot */}
                {!isBotOnly && config.dailyKey && (
                    <Box sx={{ mb: 2 }}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-end",
                                mb: 1,
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: 1,
                                        color: "text.secondary",
                                        display: "block",
                                    }}
                                >
                                    Credits Remaining Today
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {daily.toLocaleString()}{" "}
                                    <Typography
                                        component="span"
                                        variant="body1"
                                        sx={{ color: "text.secondary", fontWeight: 400 }}
                                    >
                                        / {dailyAssign.toLocaleString()}
                                    </Typography>
                                </Typography>
                            </Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: config.color,
                                }}
                            >
                                {percentClamped}% Remaining
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={percentClamped}
                            sx={{
                                height: 8,
                                borderRadius: 1,
                                backgroundColor: "rgba(0,0,0,0.06)",
                                "& .MuiLinearProgress-bar": {
                                    borderRadius: 1,
                                    background: `linear-gradient(90deg, ${config.color} 0%, ${config.color}dd 100%)`,
                                },
                            }}
                        />
                    </Box>
                )}

                {/* Footer status */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        pt: 2,
                        mt: 1,
                        borderTop: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FiberManualRecordIcon
                            sx={{ fontSize: 10, color: "success.main" }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            Service Active
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                        <Typography variant="caption" color="text.secondary">
                            Live
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const CreditsInfo = () => {
    const [credits, setCredits] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchCredits = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get("user-credits");
            if (response.data?.success) {
                setCredits(response.data);
            } else {
                setCredits(null);
            }
        } catch (err) {
            console.error("Error fetching credits:", err);
            setError(err.message || "Failed to load credits");
            setCredits(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCredits();
    }, [fetchCredits]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return null;
    }

    const billingType = credits?.billing_type || credits?.data?.billing_type;
    if (billingType === "CREDIT_BASED") {
        return <CreditWalletPanel showBuy={false} />;
    }

    if (!credits?.data) {
        return null;
    }

    const { data, included_services = [] } = credits;

    // Use included_services from API response - API returns only services user has access to
    const displayOrder = ["business_email", "personal_whatsapp", "business_whatsapp", "bot_credits"];
    const cards = displayOrder
        .filter((key) => included_services.includes(key) && SERVICE_CONFIG[key])
        .map((key) => ({ key, config: SERVICE_CONFIG[key] }));

    if (cards.length === 0) {
        return null;
    }

    return (
        <Grid container spacing={3}>
            {cards.map(({ key, config }) => (
                <Grid item xs={12} sm={6} md={6} lg={4} key={key}>
                    <CreditsInfoCard serviceKey={key} config={config} data={data} />
                </Grid>
            ))}
        </Grid>
    );
};

export default CreditsInfo;
